import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Badge, Button, Card, Form, Spinner, Modal } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import Layout from "../../../universal/layout/Layout";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import DocumentsButton from "app/orderTool/components/DocumentsButton";
import { ColumnEmpty } from "app/orderTool/components/pageUi";
import EvenementModal from "./EvenementModal";
import VisiteGuidee from "app/orderTool/components/VisiteGuidee";
import {
  getPipeline,
  getEvenement,
  getEvenementPrefill,
  getEvenementConstants,
  transformEvenement,
  patchGroupedStatusPipeline,
  markDevisEnvoye,
  patchDevisTemperature,
} from "state/evenement/actions";
import { filterPipelineByCdc } from "./pipelineFilters";
import { TEMPERATURES } from "./temperatures";

// Repli si les constantes ne sont pas encore chargées (la liste vient de Evenement::CHARGES_DE_CLIENTELE).
const CHARGES_DEFAUT = ["Julie", "Valentin", "Camille"];

const aujourdhui = () => new Date().toISOString().slice(0, 10);

// Recherche texte du board : sans accents, casse, ponctuation ni espaces.
const normaliser = (texte) =>
  (texte || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    // Ni ponctuation ni espaces : « diplom » ou « diplo'm » trouvent « Diplo'Mates »
    .replace(/[^a-z0-9]/g, "");

const COLONNES = [
  { id: "a_qualifier", titre: "À qualifier", couleur: "#f0ad4e" },
  { id: "devis_a_faire", titre: "Devis à faire", couleur: "#d9534f" },
  { id: "devis_envoyes", titre: "Devis envoyés", couleur: "#5bc0de" },
  { id: "devis_valides", titre: "Devis validés", couleur: "#429853" },
  { id: "a_facturer", titre: "À facturer", couleur: "#1d1d1b" },
];

const STATUS_BY_COLUMN = { devis_valides: "102", a_facturer: "103" };

const heuresDepuis = (dateStr) => (dateStr ? (Date.now() - new Date(dateStr).getTime()) / 3600000 : null);

const slaBadge = (evenement) => {
  if (evenement.date_rappel_effectue) {
    return <Badge variant="success">Rappel fait</Badge>;
  }
  const h = heuresDepuis(evenement.date_demande_entrante);
  if (h === null) return null;
  if (h <= 2) return <Badge variant="success">SLA 2h</Badge>;
  if (h <= 24) return <Badge variant="warning">&gt; 2h</Badge>;
  if (h <= 48) return <Badge variant="danger">&gt; 24h</Badge>;
  return <Badge variant="dark">&gt; 48h ⚠</Badge>;
};

// Séparateur de milliers pour lire les totaux d'un coup d'œil : « 12 500,00 € »
// et non « 12500.00 € » (retour Camille 15/09).
const EUROS_FORMAT = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const euros = (centimes) => (centimes ? `${EUROS_FORMAT.format(centimes / 100)} €` : null);

const dateFr = (valeur) => (valeur ? new Date(valeur).toLocaleDateString("fr-FR") : null);

/**
 * Pastilles reprises de l'agenda (retour Camille 16/09) : mêmes couleurs, même
 * signification — service en rouge, zéro déchet en vert, SAV en bleu.
 */
const PASTILLES = [
  { cle: "service", couleur: "red", libelle: "Avec service" },
  { cle: "zero_dechet", couleur: "#94c42c", libelle: "Zéro déchet" },
  { cle: "sav_in_progress", couleur: "#4a90d9", libelle: "SAV en cours" },
];

const Pastilles = ({ carte }) => {
  const actives = PASTILLES.filter(({ cle }) => carte[cle] === true || carte[cle] === 1);
  if (actives.length === 0) return null;

  return (
    <span className="ml-1">
      {actives.map(({ cle, couleur, libelle }) => (
        <span
          key={cle}
          title={libelle}
          style={{
            width: 12,
            height: 12,
            borderRadius: 50,
            backgroundColor: couleur,
            display: "inline-block",
            marginLeft: 3,
          }}
        />
      ))}
    </span>
  );
};

/**
 * Visite guidée du board. Chaque étape vise un `data-visite` posé plus bas ;
 * une étape dont la cible n'est pas à l'écran (colonne vide) est ignorée.
 */
const ETAPES_VISITE = [
  {
    cible: "board",
    titre: "Le pipeline en un coup d'œil",
    texte:
      "Chaque carte est une affaire : une demande entrante à gauche, puis son devis qui avance vers la droite au fil de sa vie. Les cinq colonnes se lisent dans l'ordre : à qualifier, devis à faire, envoyés, validés, à facturer.",
    placement: "bas",
  },
  {
    cible: "colonne-a_qualifier",
    titre: "Tout commence ici",
    texte:
      "Une demande qui arrive se pose dans « À qualifier ». Cliquez la carte pour ouvrir le brief et poser les questions au client. Le badge « Prêt à transformer » apparaît quand les informations minimales sont là.",
  },
  {
    cible: "nouvel-evenement",
    titre: "Créer une demande",
    texte:
      "Un client appelle ou écrit ? Créez l'événement ici. Le brief guide la prise d'informations, et le devis se génère ensuite tout seul à partir de ce que vous avez saisi.",
  },
  {
    cible: "colonne-devis_a_faire",
    titre: "Faire glisser pour avancer",
    texte:
      "Une carte se déplace d'une colonne à l'autre par glisser-déposer, et son statut suit. Déposer un événement dans « Devis à faire » le transforme en commande groupée, avec une prestation par format attendu.",
  },
  {
    cible: "outils-devis_envoyes",
    titre: "Trier et filtrer une colonne",
    texte:
      "Chaque colonne de devis a son tri (prix, date de création, date d'événement) et ses filtres de date : création à partir du, événement du … au … (même date des deux côtés pour un seul jour). Le total affiché en tête de colonne se recalcule sur les cartes réellement affichées.",
  },
  {
    cible: "filtre-cdc",
    titre: "Ne voir que vos dossiers",
    texte:
      "Ce filtre restreint tout le board à un chargé de clientèle, totaux compris. Pratique pour faire son point du matin sans le bruit des affaires des autres.",
  },
];

const PipelineB2b = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const pipeline = useSelector((state) => state.evenement.pipeline);
  const constants = useSelector((state) => state.evenement.constants);
  const [chargeFiltre, setChargeFiltre] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvenement, setModalEvenement] = useState(null);
  const [modalPrefill, setModalPrefill] = useState(null);
  const [modalLoadingDetail, setModalLoadingDetail] = useState(false);
  const [openingEventId, setOpeningEventId] = useState(null);
  const [boardBusy, setBoardBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState("");
  // Filtres / tris par colonne
  const [sortAFaire, setSortAFaire] = useState("objectif_desc"); // Devis à faire
  const [sortEnvoyes, setSortEnvoyes] = useState(""); // Devis envoyés
  const [envCreaMin, setEnvCreaMin] = useState(""); // filtre Devis envoyés : date création ≥
  const [envEvtMin, setEnvEvtMin] = useState(""); // filtre Devis envoyés : date événement ≥
  // Borne haute de la date d'événement : sans elle, impossible de retrouver les devis
  // en attente pour une date donnée (retour terrain Keep 26/10 — du = au = la date).
  const [envEvtMax, setEnvEvtMax] = useState("");
  // Retour Camille 02/07 : mêmes tri et filtres sur « À facturer » que sur « Devis envoyés »
  const [sortAFacturer, setSortAFacturer] = useState("");
  const [facCreaMin, setFacCreaMin] = useState("");
  const [facEvtMin, setFacEvtMin] = useState("");
  const [facEvtMax, setFacEvtMax] = useState("");
  // Retour Camille 15/09 : et le même bloc sur « Devis validés »
  const [sortValides, setSortValides] = useState("");
  const [valCreaMin, setValCreaMin] = useState("");
  const [valEvtMin, setValEvtMin] = useState("");
  const [valEvtMax, setValEvtMax] = useState("");

  // Tri + filtres de date, identiques sur les trois colonnes de devis travaillés.
  const outilsParColonne = {
    devis_envoyes: {
      sort: sortEnvoyes, setSort: setSortEnvoyes,
      creaMin: envCreaMin, setCreaMin: setEnvCreaMin,
      evtMin: envEvtMin, setEvtMin: setEnvEvtMin,
      evtMax: envEvtMax, setEvtMax: setEnvEvtMax,
    },
    devis_valides: {
      sort: sortValides, setSort: setSortValides,
      creaMin: valCreaMin, setCreaMin: setValCreaMin,
      evtMin: valEvtMin, setEvtMin: setValEvtMin,
      evtMax: valEvtMax, setEvtMax: setValEvtMax,
    },
    a_facturer: {
      sort: sortAFacturer, setSort: setSortAFacturer,
      creaMin: facCreaMin, setCreaMin: setFacCreaMin,
      evtMin: facEvtMin, setEvtMin: setFacEvtMin,
      evtMax: facEvtMax, setEvtMax: setFacEvtMax,
    },
  };

  const [visiteOuverte, setVisiteOuverte] = useState(false);
  // Recherche sur tout le board (numéro, client, entreprise, nom d'événement)
  const [recherche, setRecherche] = useState("");
  // « Devis envoyés » restreint aux relances dues (aujourd'hui ou en retard)
  const [relancesDuJour, setRelancesDuJour] = useState(false);
  // Température posée depuis le board, affichée sans attendre le rechargement
  const [temperatures, setTemperatures] = useState({});

  // Board calé sur la hauteur restante de l'écran : chaque colonne défile
  // dans sa propre zone au lieu d'allonger toute la page. Recalculé au
  // redimensionnement (le bandeau alpha et l'en-tête décalent le haut du board).
  const boardRef = useRef(null);
  const [boardHeight, setBoardHeight] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      if (!boardRef.current) return;
      const top = boardRef.current.getBoundingClientRect().top + window.scrollY;
      setBoardHeight(Math.max(window.innerHeight - top - 15, 400));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const reload = useCallback(() => {
    dispatch(getPipeline());
  }, [dispatch]);

  useEffect(() => {
    reload();
    if (!constants) {
      dispatch(getEvenementConstants());
    }
  }, [reload, dispatch, constants]);

  // Board ouvert sur les dossiers du chargé de clientèle connecté, et le filtre l'affiche
  // (retour Keep #20, relu par Romain le 30/10) ; « Tous » reste à un clic.
  const filtreInitialise = useRef(false);
  useEffect(() => {
    if (!filtreInitialise.current && constants) {
      filtreInitialise.current = true;
      if (constants.chargeConnecte) {
        setChargeFiltre(constants.chargeConnecte);
      }
    }
  }, [constants]);

  // Titre d'onglet propre à la page (l'app affiche « Companion… » par défaut).
  useEffect(() => {
    const prev = document.title;
    document.title = "Pipeline B2B — Le Moulin";
    return () => { document.title = prev; };
  }, []);

  const filteredPipeline = useMemo(() => {
    const parCdc = filterPipelineByCdc(pipeline, chargeFiltre);
    const q = normaliser(recherche.trim());
    if (!q) return parCdc;
    const contient = (...champs) => champs.some((c) => normaliser(c).includes(q));
    const colonnes = {};
    Object.entries(parCdc.colonnes || {}).forEach(([key, cards]) => {
      colonnes[key] = (cards || []).filter((c) =>
        contient(c.order_number, c.company, c.firstname, c.lastname, `${c.firstname || ""} ${c.lastname || ""}`, c.evenement_nom)
      );
    });
    return {
      ...parCdc,
      aQualifier: (parCdc.aQualifier || []).filter((e) =>
        contient(e.reference, e.contact_nom, e.entreprise?.name, e.nom)
      ),
      colonnes,
    };
  }, [pipeline, chargeFiltre, recherche]);

  // Devis envoyés dont la prochaine relance est due (aujourd'hui ou en retard)
  const relancesDues = useMemo(
    () => ((filteredPipeline.colonnes || {}).devis_envoyes || []).filter(
      (c) => c.prochaine_relance && c.prochaine_relance <= aujourdhui()
    ),
    [filteredPipeline]
  );

  const changerTemperature = async (card, temperature) => {
    const valeur = temperature || null;
    setTemperatures((t) => ({ ...t, [card.id]: valeur }));
    const resp = await dispatch(patchDevisTemperature(card.id, valeur));
    if (resp?.error || resp?.success === false) {
      setTemperatures((t) => ({ ...t, [card.id]: card.temperature || null }));
      toast.error(resp?.message || "Température non enregistrée");
    }
  };

  const openEvenementDetail = (evenementCard) => {
    setModalPrefill(null);
    setModalEvenement(evenementCard);
    setModalOpen(true);
    setModalLoadingDetail(true);
    setOpeningEventId(evenementCard.id);
    dispatch(getEvenement(evenementCard.id)).then((resp) => {
      if (resp?.evenement) {
        setModalEvenement(resp.evenement);
      } else if (resp?.error) {
        toast.error("Impossible de charger l'événement");
      }
    }).finally(() => {
      setModalLoadingDetail(false);
      setOpeningEventId(null);
    });
  };

  const openNewEvenement = () => {
    setModalEvenement(null);
    setModalPrefill(null);
    setModalLoadingDetail(false);
    setModalOpen(true);
  };

  // Carte devis sans événement → popup proposant créer la fiche ou éditer le devis.
  const [choiceCard, setChoiceCard] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const handleCreerFicheDepuisDevis = async (card) => {
    setPrefillLoading(true);
    const resp = await dispatch(getEvenementPrefill(card.id));
    setPrefillLoading(false);
    const body = resp?.result || resp || {};
    const prefill = body.prefill;
    if (resp?.error || body.success === false || !prefill) {
      toast.error(body.message || "Pré-remplissage impossible");
      return;
    }
    setChoiceCard(null);
    setModalEvenement(null);
    setModalPrefill(prefill);
    setModalLoadingDetail(false);
    setModalOpen(true);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.droppableId === result.source.droppableId) {
      return;
    }
    const from = result.source.droppableId;
    const to = result.destination.droppableId;
    const [kind, idStr] = result.draggableId.split("-");
    const id = parseInt(idStr, 10);

    if (kind === "evt") {
      if (to === "devis_a_faire") {
        setBoardBusy(true);
        setBusyMessage("Transformation en cours…");
        const resp = await dispatch(transformEvenement(id));
        if (resp?.error || resp?.success === false) {
          toast.error(resp?.message || "Transformation impossible — complétez le questionnaire");
          const card = pipeline.aQualifier.find((e) => e.id === id);
          if (card) {
            openEvenementDetail(card);
          }
        } else {
          toast.success("Commande groupée créée");
        }
        setBoardBusy(false);
        reload();
      } else {
        toast.info("Un événement se qualifie (clic) ou se transforme en le déposant sur « Devis à faire »");
      }
      return;
    }

    // « Devis à faire » → « Devis envoyés » sans rouvrir ni resauvegarder le devis : on pose la
    // date d'envoi sur l'événement lié (relances calées), qui suffit à classer la carte
    // (retour terrain Keep, 26/10/2026).
    if (from === "devis_a_faire" && to === "devis_envoyes") {
      const card = (pipeline.colonnes?.devis_a_faire || []).find((c) => c.id === id);
      if (!card?.evenement_id) {
        toast.info("Ce devis n'a pas de fiche événement : créez-la (clic sur la carte) ou sauvegardez le devis pour le passer en « envoyé »");
        return;
      }
      setBoardBusy(true);
      setBusyMessage("Passage en « Devis envoyés »…");
      const resp = await dispatch(markDevisEnvoye(card.evenement_id));
      setBoardBusy(false);
      if (resp?.error || resp?.success === false) {
        toast.error(resp?.message || "Passage en « envoyé » impossible");
      } else {
        toast.success("Devis passé en « envoyé », relances programmées");
      }
      reload();
      return;
    }
    const targetStatus = STATUS_BY_COLUMN[to];
    if (!targetStatus) {
      toast.info("Déplacement non autorisé depuis cette colonne");
      return;
    }
    setBoardBusy(true);
    setBusyMessage("Mise à jour du statut…");
    const resp = await dispatch(patchGroupedStatusPipeline(id, targetStatus));
    setBoardBusy(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Changement de statut refusé");
    } else {
      toast.success("Statut mis à jour");
    }
    reload();
  };

  const renderEvenementCard = (evenement, index) => {
    const isOpening = openingEventId === evenement.id;
    // La typologie ayant disparu du brief (redite avec l'Offre, retour Camille
    // 15/09), l'orientation e-commerce se lit désormais sur les formats attendus.
    const ecommerceOnly =
      (evenement.format_attendu || []).length > 0 &&
      // Seuls les plateaux repas se commandent en ligne (retour Keep #11, 30/10/2026)
      evenement.format_attendu.every((f) => f === "plateaux repas");
    return (
      <Draggable key={`evt-${evenement.id}`} draggableId={`evt-${evenement.id}`} index={index}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <Card
              className="mb-2"
              style={{
                cursor: isOpening ? "wait" : "pointer",
                opacity: isOpening ? 0.65 : 1,
                pointerEvents: isOpening ? "none" : "auto",
                minHeight: 124,
              }}
              onClick={() => openEvenementDetail(evenement)}
            >
              <Card.Body className="p-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <b>{evenement.reference}</b>
                    <Pastilles carte={evenement} />
                  </span>
                  {isOpening ? (
                    <Spinner animation="border" size="sm" variant="success" />
                  ) : (
                    slaBadge(evenement)
                  )}
                </div>
                {evenement.nom && <div><b style={{ fontSize: "0.85rem" }}>{evenement.nom}</b></div>}
                <small>
                  {evenement.contact_nom || "Contact ?"}
                  {evenement.entreprise?.name ? ` — ${evenement.entreprise.name}` : ""}
                  <br />
                  {evenement.date_evenement ? new Date(evenement.date_evenement).toLocaleDateString("fr-FR") : "Date ?"}
                  {(evenement.dates_supplementaires || []).length > 0 && ` (+${evenement.dates_supplementaires.length} date${evenement.dates_supplementaires.length > 1 ? "s" : ""})`}
                  {" · "}
                  {evenement.nb_convives ? `${evenement.nb_convives} pers` : "Convives ?"}
                  {evenement.budget_centimes ? ` · ${euros(evenement.budget_centimes)}` : ""}
                </small>
                {evenement.date_decision && (
                  <div><small className="text-muted">🎯 Objectif envoi : {new Date(evenement.date_decision).toLocaleDateString("fr-FR")}</small></div>
                )}
                <div className="mt-1">
                  {evenement.charge_de_clientele && <Badge variant="secondary" className="mr-1">{evenement.charge_de_clientele}</Badge>}
                  {evenement.is_above_threshold && <Badge variant="info" className="mr-1">≥ 5 000 €</Badge>}
                  {ecommerceOnly && <Badge variant="warning">→ e-commerce</Badge>}
                  {(evenement.missing_fields || []).length === 0 && <Badge variant="success">Prêt à transformer</Badge>}
                </div>
              </Card.Body>
            </Card>
          </div>
        )}
      </Draggable>
    );
  };

  /**
   * Ligne de suivi d'une carte devis (retour Camille 15/09) : tant que le devis
   * est à faire, on rappelle la date objectif d'envoi ; une fois envoyé, c'est
   * la prochaine relance prévue qui compte.
   */
  const renderSuiviLigne = (card, colonneId) => {
    if (colonneId === "devis_a_faire" && card.objectif_envoi) {
      return (
        <div>
          <small className="text-muted">🎯 Objectif envoi : {dateFr(card.objectif_envoi)}</small>
        </div>
      );
    }
    if (colonneId === "devis_envoyes" && card.prochaine_relance) {
      const enRetard = card.prochaine_relance < new Date().toISOString().slice(0, 10);
      return (
        <div>
          <small className={enRetard ? "text-danger" : "text-muted"}>
            🔔 Relance prévue : {dateFr(card.prochaine_relance)}
          </small>
        </div>
      );
    }
    return null;
  };

  const renderDevisCard = (card, index, colonneId) => (
    <Draggable key={`dev-${card.id}`} draggableId={`dev-${card.id}`} index={index}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
          <Card
            className="mb-2"
            style={{ cursor: "pointer", minHeight: 124 }}
            onClick={() => card.evenement_id
              ? openEvenementDetail({ id: card.evenement_id })
              : setChoiceCard(card)}
          >
            <Card.Body className="p-2">
              <div className="d-flex justify-content-between">
                <span>
                  <b>{card.order_number}</b>
                  <Pastilles carte={card} />
                </span>
                {card.account_manager && <Badge variant="secondary">{card.account_manager}</Badge>}
              </div>
              {!card.evenement_id && (
                <div className="mt-1">
                  <Badge variant="warning" title="Ce devis n'est rattaché à aucun événement — le clic ouvre directement l'éditeur de devis">
                    Sans événement
                  </Badge>
                </div>
              )}
              {card.evenement_nom && <div><b style={{ fontSize: "0.85rem" }}>{card.evenement_nom}</b></div>}
              <small>
                {/* Entreprise ET nom du client (retour terrain Keep 26/10 : le nom manquait) */}
                {[card.company, [card.firstname, card.lastname].filter(Boolean).join(" ")].filter(Boolean).join(" — ") || "Client ?"}
                <br />
                {card.delivery_date ? new Date(card.delivery_date).toLocaleDateString("fr-FR") : "Date ?"}
                {" · "}
                {card.nb_prestations} presta{card.nb_prestations > 1 ? "s" : ""}
                {card.total_ht ? ` · ${euros(card.total_ht)} HT` : ""}
                <br />
                <span className="text-muted">Créé le {dateFr(card.created_at) || "?"}</span>
              </small>
              {renderSuiviLigne(card, colonneId)}
              {(() => {
                const temperature = card.id in temperatures ? temperatures[card.id] : card.temperature;
                const t = TEMPERATURES[temperature];
                return (
                  <Form.Control
                    as="select"
                    size="sm"
                    className="mt-1"
                    title="Température : chances de signature, pour la prod et le service"
                    style={{ width: "auto", height: 24, padding: "0 4px", fontSize: "0.72rem", color: t ? t.couleur : "#888", fontWeight: t ? "bold" : "normal" }}
                    value={temperature || ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => changerTemperature(card, e.target.value)}
                  >
                    <option value="">Température ?</option>
                    {Object.entries(TEMPERATURES).map(([k, v]) => <option key={k} value={k}>{v.libelle}</option>)}
                  </Form.Control>
                );
              })()}
              {card.documents && card.documents.length > 0 && (
                <div className="mt-1">
                  <DocumentsButton documents={card.documents} stopPropagation />
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </Draggable>
  );

  const cardsByColumn = {
    a_qualifier: filteredPipeline.aQualifier,
    ...filteredPipeline.colonnes,
  };

  // Tri + filtres communs aux colonnes de devis (Devis envoyés, À facturer).
  const trierDevis = (list, sort, creaMin, evtMin, evtMax) => {
    if (creaMin) list = list.filter((c) => c.created_at && c.created_at >= creaMin);
    if (evtMin) list = list.filter((c) => c.delivery_date && c.delivery_date >= evtMin);
    if (evtMax) list = list.filter((c) => c.delivery_date && c.delivery_date <= evtMax);
    if (sort === "prix_desc") list.sort((a, b) => (b.total_ht || 0) - (a.total_ht || 0));
    else if (sort === "prix_asc") list.sort((a, b) => (a.total_ht || 0) - (b.total_ht || 0));
    else if (sort === "crea_desc") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "evt_desc") list.sort((a, b) => (b.delivery_date || "").localeCompare(a.delivery_date || ""));
    else if (sort === "evt_asc") list.sort((a, b) => (a.delivery_date || "9999").localeCompare(b.delivery_date || "9999"));
    // Relance la plus proche d'abord, devis sans relance prévue en fin de liste
    else if (sort === "relance_asc") list.sort((a, b) => (a.prochaine_relance || "9999").localeCompare(b.prochaine_relance || "9999"));
    return list;
  };

  // Filtres + tris par colonne.
  const displayCards = (id, cards) => {
    let list = Array.isArray(cards) ? [...cards] : [];
    const outils = outilsParColonne[id];
    if (outils) {
      if (id === "devis_envoyes" && relancesDuJour) {
        list = list.filter((c) => c.prochaine_relance && c.prochaine_relance <= aujourdhui());
      }
      list = trierDevis(list, outils.sort, outils.creaMin, outils.evtMin, outils.evtMax);
    } else if (id === "devis_a_faire") {
      if (sortAFaire === "objectif_desc") list.sort((a, b) => (b.objectif_envoi || "").localeCompare(a.objectif_envoi || ""));
      else if (sortAFaire === "objectif_asc") list.sort((a, b) => (a.objectif_envoi || "").localeCompare(b.objectif_envoi || ""));
      else if (sortAFaire === "evt_asc") list.sort((a, b) => (a.delivery_date || "9999").localeCompare(b.delivery_date || "9999"));
      else if (sortAFaire === "evt_desc") list.sort((a, b) => (b.delivery_date || "").localeCompare(a.delivery_date || ""));
    }
    return list;
  };

  // Retour Camille 02/07 : total € du pipe par colonne, recalculé sur les cartes
  // RÉELLEMENT affichées — il suit donc les filtres de colonne et le filtre CDC.
  // Colonnes de gauche = événements (budget annoncé, estimatif) ; colonnes de devis
  // = total HT réel. Les deux ne se mélangent pas, d'où le libellé distinct.
  const totalColonne = (id, cards) => {
    const estimatif = id === "a_qualifier" || id === "devis_a_faire";
    const montant = (cards || []).reduce(
      (acc, c) => acc + (estimatif ? (c.budget_centimes || 0) : (c.total_ht || 0)),
      0
    );
    return { montant, estimatif };
  };

  const boardLoading = pipeline.isLoading && !pipeline.isInitialized;

  return (
    <Layout>
      <div style={{ padding: "15px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">
            Pipeline commercial B2B
            {pipeline.isLoading && pipeline.isInitialized && (
              <Spinner animation="border" size="sm" variant="success" className="ml-2" />
            )}
          </h4>
          <div className="d-flex align-items-center">
            <Form.Control
              type="search"
              size="sm"
              style={{ width: "260px" }}
              className="mr-2"
              placeholder="Rechercher : n° de devis, client, entreprise…"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <Button
              variant="outline-success"
              size="sm"
              className="mr-2"
              onClick={() => setVisiteOuverte(true)}
              title="Revoir la visite guidée de l'écran"
            >
              Comment ça marche&nbsp;?
            </Button>
            <Form.Control
              as="select"
              size="sm"
              style={{ width: "220px" }}
              className="mr-2"
              data-visite="filtre-cdc"
              value={chargeFiltre}
              onChange={(e) => setChargeFiltre(e.target.value)}
              disabled={boardBusy}
            >
              <option value="">Tous les chargés de clientèle</option>
              {(constants?.chargesDeClientele || CHARGES_DEFAUT).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Control>
            <Button
              variant="success"
              size="sm"
              onClick={openNewEvenement}
              disabled={boardBusy}
              data-visite="nouvel-evenement"
            >
              + Nouvel événement
            </Button>
          </div>
        </div>

        {relancesDues.length > 0 && (
          <div
            className="mb-2 p-2"
            style={{ background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6, fontSize: "0.85rem" }}
          >
            🔔 <b>{relancesDues.length} devis à relancer aujourd'hui</b>
            {" : "}
            {relancesDues.slice(0, 8).map((c) => (
              `${c.order_number} (${c.company || [c.firstname, c.lastname].filter(Boolean).join(" ") || "?"}${c.prochaine_relance < aujourdhui() ? ", en retard" : ""})`
            )).join(" · ")}
            {relancesDues.length > 8 ? " …" : ""}
            {!relancesDuJour && (
              <Button variant="link" size="sm" className="p-0 ml-2" onClick={() => setRelancesDuJour(true)}>
                Ne montrer qu'eux dans « Devis envoyés »
              </Button>
            )}
          </div>
        )}

        <div style={{ position: "relative" }}>
          <LoadingOverlay
            show={boardLoading || boardBusy}
            message={boardBusy ? busyMessage : "Chargement du pipeline…"}
            minHeight={320}
          />
          <DragDropContext onDragEnd={handleDragEnd}>
            {/* Pas d'overflow sur le board lui-même : react-beautiful-dnd n'accepte
                qu'un conteneur défilant par colonne, et c'est la zone de cartes. */}
            <div
              ref={boardRef}
              style={{ display: "flex", gap: "12px", alignItems: "stretch", height: boardHeight || undefined }}
              data-visite="board"
            >
              {COLONNES.map((colonne) => {
                const colCards = displayCards(colonne.id, cardsByColumn[colonne.id]);
                return (
                <div
                  key={colonne.id}
                  style={{ flex: "1 0 19%", minWidth: "230px", display: "flex", flexDirection: "column", minHeight: 0 }}
                  data-visite={`colonne-${colonne.id}`}
                >
                  <div style={{
                    background: colonne.couleur, color: "#fff", padding: "8px 12px",
                    borderRadius: "6px 6px 0 0", fontWeight: "bold",
                  }}>
                    {colonne.titre}{" "}
                    <Badge variant="light">{colCards.length}</Badge>
                    {(() => {
                      const { montant, estimatif } = totalColonne(colonne.id, colCards);
                      if (!montant) return null;
                      return (
                        <div style={{ fontWeight: "normal", fontSize: "0.72rem", opacity: 0.9 }}>
                          {euros(montant)} {estimatif ? "de budget annoncé" : "HT"}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Zone outils : même hauteur sur toutes les colonnes pour aligner
                      la 1re rangée de cartes (overflow des dates corrigé via minWidth:0). */}
                  <div
                    style={{ background: "#eee", padding: "4px 6px", fontSize: "0.72rem", minHeight: 72 }}
                    data-visite={`outils-${colonne.id}`}
                  >
                    {colonne.id === "devis_a_faire" && (
                      <Form.Control as="select" size="sm" value={sortAFaire} onChange={(e) => setSortAFaire(e.target.value)}>
                        <option value="objectif_desc">Objectif d'envoi ↓</option>
                        <option value="objectif_asc">Objectif d'envoi ↑</option>
                        <option value="evt_asc">Date d'événement ↑</option>
                        <option value="evt_desc">Date d'événement ↓</option>
                        <option value="">Par défaut</option>
                      </Form.Control>
                    )}
                    {outilsParColonne[colonne.id] && (() => {
                      const outils = outilsParColonne[colonne.id];
                      return (
                        <>
                          <Form.Control as="select" size="sm" className="mb-1" value={outils.sort} onChange={(e) => outils.setSort(e.target.value)}>
                            <option value="">Tri…</option>
                            <option value="prix_desc">Prix ↓</option>
                            <option value="prix_asc">Prix ↑</option>
                            <option value="crea_desc">Création ↓</option>
                            <option value="evt_desc">Événement ↓</option>
                            <option value="evt_asc">Événement ↑</option>
                            <option value="relance_asc">Prochaine relance ↑</option>
                          </Form.Control>
                          {colonne.id === "devis_envoyes" && (
                            <Form.Check
                              type="checkbox"
                              id="relances-du-jour"
                              className="mb-1"
                              label={`À relancer aujourd'hui (${relancesDues.length})`}
                              checked={relancesDuJour}
                              onChange={(e) => setRelancesDuJour(e.target.checked)}
                            />
                          )}
                          <small className="text-muted d-block">Créé à partir du</small>
                          <Form.Control type="date" size="sm" className="mb-1" title="Création ≥" value={outils.creaMin} onChange={(e) => outils.setCreaMin(e.target.value)} />
                          <small className="text-muted d-block">Événement du … au …</small>
                          <div className="d-flex" style={{ gap: 4 }}>
                            <Form.Control type="date" size="sm" title="Événement ≥" style={{ flex: 1, minWidth: 0 }} value={outils.evtMin} onChange={(e) => outils.setEvtMin(e.target.value)} />
                            <Form.Control type="date" size="sm" title="Événement ≤" style={{ flex: 1, minWidth: 0 }} value={outils.evtMax} onChange={(e) => outils.setEvtMax(e.target.value)} />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <Droppable droppableId={colonne.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          background: snapshot.isDraggingOver ? "#dff0d8" : "#f5f5f5",
                          padding: "8px", borderRadius: "0 0 6px 6px",
                          flex: 1, minHeight: 0, overflowY: "auto",
                        }}
                      >
                        {colCards.map((card, index) =>
                          colonne.id === "a_qualifier"
                            ? renderEvenementCard(card, index)
                            : renderDevisCard(card, index, colonne.id)
                        )}
                        {!snapshot.isDraggingOver && colCards.length === 0 && (
                          <ColumnEmpty>Aucun élément</ColumnEmpty>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>

        <VisiteGuidee etapes={ETAPES_VISITE} show={visiteOuverte} onClose={() => setVisiteOuverte(false)} />

      <EvenementModal
          show={modalOpen}
          evenement={modalEvenement}
          prefill={modalPrefill}
          constants={constants}
          chargeParDefaut={chargeFiltre}
          loadingDetail={modalLoadingDetail}
          onClose={() => { setModalOpen(false); setModalLoadingDetail(false); setModalPrefill(null); }}
          onSaved={reload}
        />

        {/* Carte devis sans événement : créer la fiche (pré-remplie) ou éditer le devis */}
        <Modal show={!!choiceCard} onHide={() => setChoiceCard(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Devis sans événement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-2">
              Le devis <b>{choiceCard?.order_number}</b> n'est rattaché à aucun événement.
              Que voulez-vous faire ?
            </p>
            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
              Créer la fiche événement pré-remplira les champs depuis le devis et le liera automatiquement.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary"
              onClick={() => { const c = choiceCard; setChoiceCard(null); history.push(`/commande?order_id=${c.id}&order_type=v2&from=pipeline`); }}>
              ↗ Éditer le devis
            </Button>
            <Button variant="success" disabled={prefillLoading}
              onClick={() => handleCreerFicheDepuisDevis(choiceCard)}>
              {prefillLoading ? <><Spinner animation="border" size="sm" className="mr-1" /> Préparation…</> : "Créer la fiche événement"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default PipelineB2b;
